import React, { useState, useEffect, useRef } from 'react';
import { Autocomplete } from '@react-google-maps/api';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Chip,
  Dialog,
  DialogContent,
  Button,
  Grid2,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HandymanMapWidget from '../../../components/HandymanMapWidget/HandymanMapWidget';
import GoogleMapsWrapper from '../../../pages/UserProfile/components/UserDetails/GoogleMapsWrapper';
import {
  ButtonTertiary,
  ButtonTertiaryIcon,
  GlassColors,
  GlassTextField,
  NeumorphicButton,
} from '../../../components/glassmorphism/GlassmorphismStyles';

interface SearchOnMapPopupProps {
  open: boolean;
  onClose: () => void;
  onApplyFilters: (
    address: string,
    radius: number,
    coordinates: { lat: number; lng: number } | null,
  ) => void;
  initialAddress?: string;
  initialCoordinates?: { lat: number; lng: number } | null;
  initialRadius?: number;
  handymanCount?: number;
  availableCount?: number;
  searchArea?: string;
  isDark: boolean;
}

const SearchOnMapPopup: React.FC<SearchOnMapPopupProps> = ({
  open,
  onClose,
  onApplyFilters,
  initialAddress = '',
  initialCoordinates = null,
  initialRadius = 10,
  handymanCount = 0,
  availableCount = 0,
  searchArea = 'Current Location',
  isDark,
}) => {
  const [address, setAddress] = useState(initialAddress);
  const [coordinates, setCoordinates] = useState<{
    lat: number;
    lng: number;
  } | null>(initialCoordinates);
  const [radius, setRadius] = useState(initialRadius);
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  // Handle touch events for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    e.preventDefault();
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!isDragging || !sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const x = e.touches[0].clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newRadius = Math.min(200, Math.max(1, Math.round(percentage * 200)));
    setRadius(newRadius);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Add touch event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('touchmove', handleTouchMove, {
        passive: false,
      });
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging]);

  useEffect(() => {
    if (open) {
      setAddress(initialAddress);
      setCoordinates(initialCoordinates);
      setRadius(initialRadius);
    }
  }, [open, initialAddress, initialCoordinates, initialRadius]);

  const handleApply = () => {
    onApplyFilters(address, radius, coordinates);
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  // Handle mouse down on slider thumb
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    e.preventDefault();
  };

  // Handle mouse move for dragging
  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newRadius = Math.min(200, Math.max(1, Math.round(percentage * 200))); // 1-200km
    setRadius(newRadius);
  };

  // Handle mouse up to stop dragging
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add event listeners for mouse move and up
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Handle click on slider track
  const handleSliderClick = (e: React.MouseEvent) => {
    if (!sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newRadius = Math.min(200, Math.max(1, Math.round(percentage * 200)));
    setRadius(newRadius);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableScrollLock
      maxWidth={false}
      sx={{ zIndex: 9999999999 }}
      PaperProps={{
        sx: {
          width: { xs: '100%', md: '760px' },
          height: { xs: '100%', md: 'calc(100vh - 110px)' },
          maxHeight: 'none',
          maxWidth: 'none',
          borderRadius: { xs: 0, md: '32px' },
          background: isDark
            ? GlassColors.dark.primaryBackground
            : GlassColors.light.primaryBackground,
          boxShadow: `
            0px 0px 16px 0px #5C5C5C4D inset,
            0px 0px 3px 0px #FFFFFF66 inset,
            -1px -1px 0.5px -1px #FFFFFF inset,
            1px 1px 0.5px -1px #FFFFFF66 inset,
            -1px -1px 0px -0.5px #FFFFFF80 inset,
            1px 1px 0px -0.5px #FFFFFF99 inset,
            0px 1px 8px 0px #0000001F,
            0px 0px 2px 0px #0000001A
           `,
          backdropFilter: 'blur(126px)',
          position: 'relative',
          margin: { xs: 0, md: 'auto' },
          '&&': {
            mt: { xs: 0, md: '100px' },
            borderRadius: { xs: 0, md: '32px' },
          },
        },
      }}
    >
      <DialogContent
        sx={{
          padding: { xs: '16px', sm: '20px', md: '24px' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: { xs: '16px', sm: '24px', md: '32px' },
          height: '100%',
          overflow: 'auto',
          position: 'relative',
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: { xs: '16px', sm: '20px', md: '24px' },
            width: '100%',
            flex: '0 0 auto',
          }}
        >
          {/* Title and Close Button */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: { xs: '12px', sm: '16px', md: '24px' },
              width: '100%',
              minHeight: '32px',
            }}
          >
            {/* Title */}
            <Typography
              sx={{
                fontFamily: 'Montserrat',
                fontWeight: 800,
                fontSize: { xs: '18px', sm: '19px', md: '20px' },
                lineHeight: '160%',
                letterSpacing: '0.15px',
                color: '#005CAD',
                flex: 1,
                textAlign: 'center',
              }}
            >
              Search on map
            </Typography>

            {/* Close Button */}
            {/* <IconButton
              onClick={handleClose}
              sx={{
                width: { xs: '28px', sm: '30px', md: '32px' },
                height: { xs: '28px', sm: '30px', md: '32px' },
                minWidth: { xs: '28px', sm: '30px', md: '32px' },
                background: 'rgba(153, 153, 153, 0.3)',
                backgroundBlendMode: 'darken',
                boxShadow:
                  '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                backdropFilter: 'blur(6px)',
                borderRadius: '64px',
                color: '#E2F7FF',
                flexShrink: 0,
                '&:hover': {
                  background: 'rgba(153, 153, 153, 0.4)',
                },
              }}
            >
              <CloseIcon
                sx={{ fontSize: { xs: '14px', sm: '15px', md: '16px' } }}
              />
            </IconButton> */}
            <ButtonTertiaryIcon
              onClick={handleClose}
              sx={{
                position: 'absolute',
                zIndex: 1,
                width: '32px',
                height: '32px',
                top: { xs: '16px', sm: '24px' },
                right: { xs: '16px', sm: '24px' },
                '& svg': {
                  fontSize: '16px',
                  '& path': {
                    fill: '#E2F7FF',
                  },
                },
              }}
            >
              <CloseIcon />
            </ButtonTertiaryIcon>
          </Box>

          <Grid2 sx={{ width: '100%' }} container spacing='24px'>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              {/* Address Search Input (Google Places Autocomplete) */}
              <Box
                sx={{
                  width: '100%',

                  '& div': {
                    width: '100%',
                  },
                }}
              >
                <GoogleMapsWrapper>
                  <Autocomplete
                    onLoad={(ac) => {
                      autocompleteRef.current = ac;
                    }}
                    onPlaceChanged={() => {
                      const place = autocompleteRef.current?.getPlace();
                      if (place?.formatted_address) {
                        setAddress(place.formatted_address);
                        const lat = place.geometry?.location?.lat();
                        const lng = place.geometry?.location?.lng();
                        if (
                          typeof lat === 'number' &&
                          typeof lng === 'number'
                        ) {
                          setCoordinates({ lat, lng });
                        } else {
                          setCoordinates(null);
                        }
                      }
                    }}
                  >
                    <GlassTextField
                      placeholder='Search Adress'
                      value={address}
                      onChange={(e) => {
                        setAddress(e.target.value);
                        // Freeform typing invalidates previously-picked coordinates.
                        setCoordinates(null);
                      }}
                    />
                  </Autocomplete>
                </GoogleMapsWrapper>
              </Box>
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              {/* Radius Slider Section */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  width: '100%',
                }}
              >
                {/* Radius Label */}
                <Typography
                  sx={{
                    fontFamily: 'Roboto',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '143%',
                    letterSpacing: '0.17px',
                    color: '#1A1A1A',
                    mb: '8px',
                  }}
                >
                  Search Radius (km): {radius}
                </Typography>

                {/* Custom Slider */}
                <Box
                  ref={sliderRef}
                  onClick={handleSliderClick}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    padding: 0,
                    isolation: 'isolate',
                    width: '100%',
                    height: { xs: '10px', sm: '9px', md: '8px' },
                    position: 'relative',
                    cursor: 'pointer',
                  }}
                >
                  {/* Slider Track */}
                  <Box
                    sx={{
                      width: '100%',
                      height: { xs: '10px', sm: '9px', md: '8px' },
                      background: isDark ? '#1A1A1A' : '#E5E5DC',
                      boxShadow: `0px 0px 10px 0px #0000000A,
                            0px -0.5px 0.25px 0px #0000004D,
                            0px 0.5px 0.75px 0px #FFFFFF,
                            0px 2.07px 3.73px 0px #00000045 inset,
                            0px 1.08px 2.48px 0px #00000040 inset`,
                      borderRadius: '100px',
                      position: 'relative',
                      zIndex: 0,
                    }}
                  >
                    {/* Progress Bar */}
                    <Box
                      sx={{
                        position: 'absolute',
                        width: `${(radius / 200) * 100}%`,
                        height: '100%',
                        left: 0,
                        top: 0,
                        background: '#005CAD',
                        boxShadow: `
                            0px 0px 34.77px 0px #0000000D inset,
                            0px -0.5px 0.25px 0px #00000080 inset,
                            0px 0.5px 0.75px 0px #FFFFFF inset,
                            0px 2.07px 3.73px 0px #0000004D,
                            0px 2.08px 2.48px -2px #00000066,
                            0px 3.08px 2px -2px #00000054
                          `,
                        borderRadius: '100px',
                        zIndex: 1,
                      }}
                    />

                    {/* Slider Thumb */}
                    <Box
                      onMouseDown={handleMouseDown}
                      onTouchStart={handleTouchStart}
                      sx={{
                        position: 'absolute',
                        width: { xs: '28px', sm: '26px', md: '24px' },
                        height: { xs: '28px', sm: '26px', md: '24px' },
                        left: `${(radius / 200) * 100}%`,
                        top: { xs: '-9px', sm: '-8.5px', md: '-8px' },
                        transform: 'translateX(-50%)',
                        background: '#005CAD',
                        boxShadow: `
                          0px 0px 34.77px 0px #0000000D inset,
                          0px -0.5px 0.25px 0px #00000080 inset,
                          0px 0.5px 0.75px 0px #FFFFFF inset,
                          0px 2.07px 3.73px 0px #0000004D,
                          0px 2.08px 2.48px -2px #00000066,
                          0px 3.08px 2px -2px #00000054`,
                        borderRadius: '100px',
                        zIndex: 3,
                        cursor: isDragging ? 'grabbing' : 'grab',
                        transition: isDragging ? 'none' : 'all 0.2s ease',
                        touchAction: 'none',
                        '&:hover': {
                          transform: 'translateX(-50%) scale(1.1)',
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Grid2>
          </Grid2>
        </Box>

        {/* Map Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: { xs: '12px', sm: '14px', md: '16px' },
            width: '100%',
            flex: '1 1 auto',
            height: { xs: '320px', md: '100%' },
          }}
        >
          {/* Map Container */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: { xs: '12px', sm: '14px', md: '16px' },
              width: '100%',
              flex: 1,
              height: { xs: '320px', md: '100%' },
            }}
          >
            {/* Map */}
            <Box
              sx={{
                width: '100%',
                height: { xs: '320px', md: '100%' },
                borderRadius: { xs: '24px', md: '32px' },
                overflow: 'hidden',
                boxShadow: `
                0px 0px 34.77px 0px #0000000D inset,
                0px -0.5px 0.25px 0px #00000080 inset,
                0px 0.5px 0.75px 0px #FFFFFF inset,
                0px 2.07px 3.73px 0px #0000004D,
                0px 2.08px 2.48px -2px #00000066,
                0px 3.08px 2px -2px #00000054
                `,
                // '&& iframe': {
                //   height: { xs: '320px !important', md: '352px !important' },
                // },
                '&& div': {
                  maxHeight: '100vh',
                },
              }}
            >
              <HandymanMapWidget
                radius={radius}
                address={address}
                coordinates={coordinates}
                onRadiusChange={(newRadius) => setRadius(newRadius)}
                showControls={true}
              />
            </Box>
          </Box>

          {/* Footer Chips */}
          {/* <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'center',
              alignItems: 'center',
              padding: { xs: '6px', sm: '7px', md: '8px' },
              gap: { xs: '6px', sm: '7px', md: '8px' },
              width: '100%',
              minHeight: { xs: 'auto', md: '40px' },
              background: 'rgba(255, 255, 255, 0.9)',
              boxShadow:
                '0px 1.07796px 2.48379px #000000, 0px 2.07148px 3.72568px rgba(0, 0, 0, 0.4), inset 0px 0.496757px 0.745136px rgba(255, 255, 255, 0.4), inset 0px -0.496757px 0.248379px rgba(0, 0, 0, 0.3), inset 0px 0px 34.773px rgba(0, 0, 0, 0.2)',
              borderRadius: { xs: '24px', sm: '28px', md: '32px' },
            }}
          > */}
          {/* Professionals Count */}
          {/* <Chip
              label={`${handymanCount} Professionals`}
              sx={{
                width: { xs: '100%', sm: 'auto', md: '195px' },
                minWidth: { xs: 'auto', sm: '150px', md: '195px' },
                height: { xs: '28px', sm: '26px', md: '24px' },
                background: '#307ABD',
                boxShadow:
                  '0px 1.07796px 2.48379px #000000, 0px 2.07148px 3.72568px rgba(0, 0, 0, 0.4), inset 0px 0.496757px 0.745136px rgba(255, 255, 255, 0.4), inset 0px -0.496757px 0.248379px rgba(0, 0, 0, 0.3), inset 0px 0px 34.773px rgba(0, 0, 0, 0.2)',
                borderRadius: '999px',
                padding: { xs: '2px 16px', sm: '2px 20px', md: '2px 24px' },
                '& .MuiChip-label': {
                  fontFamily: 'Montserrat',
                  fontWeight: 500,
                  fontSize: { xs: '11px', sm: '11.5px', md: '12px' },
                  lineHeight: '166%',
                  letterSpacing: '0.4px',
                  color: '#E5E5DC',
                  padding: 0,
                },
              }}
            /> */}

          {/* Available Count */}
          {/* <Chip
              label={`${availableCount} Available`}
              sx={{
                width: { xs: '100%', sm: 'auto', md: '195px' },
                minWidth: { xs: 'auto', sm: '150px', md: '195px' },
                height: { xs: '28px', sm: '26px', md: '24px' },
                background: '#00BAB8',
                boxShadow:
                  '0px 1.07796px 2.48379px #000000, 0px 2.07148px 3.72568px rgba(0, 0, 0, 0.4), inset 0px 0.496757px 0.745136px rgba(255, 255, 255, 0.4), inset 0px -0.496757px 0.248379px rgba(0, 0, 0, 0.3), inset 0px 0px 34.773px rgba(0, 0, 0, 0.2)',
                borderRadius: '999px',
                padding: { xs: '2px 16px', sm: '2px 20px', md: '2px 24px' },
                '& .MuiChip-label': {
                  fontFamily: 'Montserrat',
                  fontWeight: 500,
                  fontSize: { xs: '11px', sm: '11.5px', md: '12px' },
                  lineHeight: '166%',
                  letterSpacing: '0.4px',
                  color: '#E5E5DC',
                  padding: 0,
                },
              }}
            /> */}

          {/* Search Area */}
          {/* <Chip
              icon={
                <LocationOnIcon
                  sx={{
                    fontSize: { xs: '14px', sm: '15px', md: '16px' },
                    color: '#E5E5DC',
                  }}
                />
              }
              label={searchArea}
              sx={{
                width: { xs: '100%', sm: 'auto', md: '218px' },
                minWidth: { xs: 'auto', sm: '160px', md: '218px' },
                height: { xs: '28px', sm: '26px', md: '24px' },
                background: '#307ABD',
                boxShadow:
                  '0px 1.07796px 2.48379px #000000, 0px 2.07148px 3.72568px rgba(0, 0, 0, 0.4), inset 0px 0.496757px 0.745136px rgba(255, 255, 255, 0.4), inset 0px -0.496757px 0.248379px rgba(0, 0, 0, 0.3), inset 0px 0px 34.773px rgba(0, 0, 0, 0.2)',
                borderRadius: '999px',
                padding: { xs: '2px 16px', sm: '2px 20px', md: '2px 24px' },
                '& .MuiChip-label': {
                  fontFamily: 'Montserrat',
                  fontWeight: 500,
                  fontSize: { xs: '11px', sm: '11.5px', md: '12px' },
                  lineHeight: '166%',
                  letterSpacing: '0.4px',
                  color: '#E5E5DC',
                  padding: 0,
                },
                '& .MuiChip-icon': {
                  margin: 0,
                  marginRight: { xs: '3px', sm: '3.5px', md: '4px' },
                },
              }}
            /> */}
          {/* </Box> */}
        </Box>
        {/* Apply Button - Mobile and Small Screens */}
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
            flex: '0 0 auto',
          }}
        >
          <ButtonTertiary onClick={onClose}>Cancel</ButtonTertiary>
          <NeumorphicButton
            onClick={handleApply}
            sx={{
              '&&': {
                fontSize: '12px',
                lineHeight: 2,
                padding: '4px 12px',
                height: 'auto',
              },
            }}
          >
            Accept location
          </NeumorphicButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SearchOnMapPopup;
